import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.signupLink}>
      <span className={css.signupLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
      <img src="/static/icon-signup-arrow.png" alt="" className={css.signupIcon} />
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.loginLink}>
      <span className={css.loginLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink className={css.topbarLink} name="InboxPage" params={{ tab: inboxTab }}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, isProvider, intl }) => {

  console.log(currentUser)
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        {currentUser?.attributes.profile.publicData.userType !== 'provider' &&
        currentUser?.attributes.profile.publicData.userType !== 'host' ? (
          <MenuItem key="PromotionQRPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('PromotionQRPage'))}
              name="PromotionQRPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.promotionQRLink" />
            </NamedLink>
          </MenuItem>
        ) : null}

        <MenuItem key="ContactsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ContactsPage'))}
            name="ContactsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.contactsPageLink" />
          </NamedLink>
        </MenuItem>

        {currentUser?.attributes.profile.publicData.isAdmin ? (
          <MenuItem key="PromotionQRStatsAdminPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('PromotionQRStatsAdminPage'))}
              name="PromotionQRStatsAdminPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.promotionStatsAdmin" />
            </NamedLink>
          </MenuItem>
        ) : null}

        {!currentUser?.attributes.profile.publicData.isAdmin &&
        currentUser?.attributes.profile.publicData.userType === 'provider' ? (
          <MenuItem key="PromotionQRStatsProviderPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('PromotionQRStatsProviderPage'))}
              name="PromotionQRStatsProviderPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.promotionStatsProvider" />
            </NamedLink>
          </MenuItem>
        ) : null}

        {!currentUser?.attributes.profile.publicData.isAdmin &&
        currentUser?.attributes.profile.publicData.userType === 'provider' ? (
          <MenuItem key="PromotionScannerPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('PromotionScannerPage'))}
              name="PromotionScannerPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.promotionScannerLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    showCustomLinks,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    isCheckout,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      isProvider={props.isProvider}
      intl={intl}
    />
  ) : null;

  const signupLinkMaybe = isCheckout || isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isCheckout || isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const searchFormMaybe = (showSearchForm && !showCustomLinks) ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />
      {searchFormMaybe}

      {showCustomLinks && !isCheckout ? (
        <CustomLinksMenu
          currentPage={currentPage}
          customLinks={customLinks}
          showSearchForm={showSearchForm}
          intl={intl}
          hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
          showCreateListingsLink={false}
        />
      ) : null}
     <div className={css.rightActions}>
  <div className={css.inboxProfileContainer}>
    {inboxLinkMaybe}
    {profileMenuMaybe}
  </div>

  {!isCheckout ? (
    <div className={css.loginSignupContainer}>
      {loginLinkMaybe}
      {signupLinkMaybe}
    </div>
  ) : null}
</div>
    </nav>
  );
};

export default TopbarDesktop;
